// SPDX-License-Identifier: AGPL-3.0-or-later

import Window from '@app/features/window/state/Window';
import {action, makeAutoObservable, onBecomeObserved, onBecomeUnobserved, reaction} from 'mobx';

class TickRegistryImpl {
	nowMs: number = Date.now();
	nowSecond: number = Math.floor(Date.now() / 1000);
	nowMinute: number = Math.floor(Date.now() / 60000);
	private intervalId: number | null = null;
	private cadence = 0;
	private observed = new Set<string>();

	constructor() {
		makeAutoObservable<this, 'intervalId' | 'observed' | 'cadence' | 'start' | 'stop' | 'tick'>(
			this,
			{intervalId: false, observed: false, cadence: false, start: action, stop: action, tick: action},
			{autoBind: true},
		);
		for (const field of ['nowMs', 'nowSecond', 'nowMinute'] as const) {
			onBecomeObserved(this, field, () => {
				this.observed.add(field);
				this.start();
			});
			onBecomeUnobserved(this, field, () => {
				this.observed.delete(field);
				this.start();
			});
		}
		reaction(
			() => Window.visible,
			(visible) => {
				if (visible) this.start();
				else this.stop();
			},
			{fireImmediately: true},
		);
	}

	private start(): void {
		if (!this.observed.size || !Window.visible) {
			this.stop();
			return;
		}
		const cadence = this.observed.has('nowMs') || this.observed.has('nowSecond') ? 1000 : 60_000;
		if (this.intervalId !== null && cadence === this.cadence) return;
		this.stop();
		this.tick();
		this.cadence = cadence;
		this.intervalId = window.setInterval(() => this.tick(), cadence);
	}

	private stop(): void {
		if (this.intervalId === null) return;
		window.clearInterval(this.intervalId);
		this.intervalId = null;
	}

	private tick(): void {
		const now = Date.now();
		this.nowMs = now;
		const second = Math.floor(now / 1000);
		const minute = Math.floor(now / 60000);
		if (second !== this.nowSecond) this.nowSecond = second;
		if (minute !== this.nowMinute) this.nowMinute = minute;
	}
}

const Tick = new TickRegistryImpl();

export function useNow(enabled: boolean): number {
	if (enabled) return Tick.nowSecond;
	return Date.now();
}

/** Shared wall-clock milliseconds for observer components; accounting uses the original start timestamp. */
export function useClock(enabled = true): number {
	return enabled ? Tick.nowMs : Date.now();
}

export default Tick;
